"use client"
import Image from "next/image"
import type { PublicPage } from "../content/content-types.ts"
import { stateCopy, stateLabel } from "../lib/state-copy.ts"
import { consumerTransactionDetails, type UserView } from "../lib/user-view.ts"
import { CatalogView } from "./catalog-view.tsx"
import { CommerceStatus, CommerceView } from "./commerce-view.tsx"
import { CustomerNumberCopy } from "./customer-number-copy.tsx"
import { HomeView } from "./home-view.tsx"
import { PhoneStatusPanel } from "./phone-status-panel.tsx"
import { FormattedPointAmount } from "./point-amount.tsx"
import { ProductView } from "./product-view.tsx"
import { RedesignBalanceView, RedesignHistoryList } from "./redesign-balance-view.tsx"

type StandardView = Exclude<
  UserView,
  {
    readonly kind: "notifications" | "preferences" | "support"
  }
>
const dateFormatter = new Intl.DateTimeFormat("ko-KR", { dateStyle: "long" })
const maskedReference = (reference: string): string => `••••-${reference.slice(-4)}`
type TransactionView = Extract<
  UserView,
  {
    readonly kind: "transaction"
  }
>
const Details = ({
  supporting = false,
  values,
}: {
  readonly supporting?: boolean
  readonly values: readonly {
    readonly label: string
    readonly value: string
  }[]
}) => (
  <dl className="detail-grid" data-detail-grid>
    {values.map((detail) => (
      <div className={supporting ? "balance-supporting-row" : undefined} data-balance-supporting={supporting ? "" : undefined} key={detail.label}>
        <dt>{detail.label}</dt>
        <dd>{detail.label === "고객번호" ? <CustomerNumberCopy value={detail.value} /> : detail.label === "원천 포인트" ? detail.value : <FormattedPointAmount value={detail.value} />}</dd>
      </div>
    ))}
  </dl>
)
const BalanceSummary = ({
  values,
}: {
  readonly values: readonly {
    readonly label: string
    readonly value: string
  }[]
}) => {
  if (values.length === 0) return null
  const available = values.find(({ label }) => label === "사용 가능")
  if (available === undefined) return <Details values={values} />
  const supporting = values.filter(({ label }) => label !== "사용 가능")
  return (
    <section aria-label="잔액 요약" className="balance-summary">
      <div className="balance-primary" data-balance-total>
        <p>{available.label}</p>
        <strong>
          <FormattedPointAmount value={available.value} />
        </strong>
      </div>
      {supporting.length === 0 ? null : <Details supporting values={supporting} />}
    </section>
  )
}
const OrderDetail = ({ view }: { readonly view: TransactionView }) => {
  const productName = view.details.find(({ label }) => label === "상품")?.value ?? "교환 상품"
  const usedPoints = view.details.find(({ label }) => label === "사용 포인트")?.value
  const orderDetails = view.details.filter(({ label }) => label !== "상품" && label !== "사용 포인트")
  return (
    <div className="order-detail-view">
      <section className="order-detail-overview">
        {view.productImageUrl === undefined ? null : (
          <div className="order-detail-media">
            <Image alt={productName} height={220} sizes="7.5rem" src={view.productImageUrl} unoptimized width={220} />
          </div>
        )}
        <div className="order-detail-copy">
          <CommerceStatus status={view.serverState} />
          <h2>{productName}</h2>
          {usedPoints === undefined ? null : (
            <strong>
              <FormattedPointAmount value={usedPoints} />
            </strong>
          )}
          <p>{stateCopy(view)}</p>
        </div>
      </section>
      {orderDetails.length === 0 ? null : <Details values={orderDetails} />}
      <div className="notice order-detail-reference">
        <p>
          처리 상태: <strong>{stateLabel(view.serverState)}</strong>
        </p>
        <p>거래 확인 번호: {maskedReference(view.reference)}</p>
      </div>
    </div>
  )
}
export const ViewBody = ({ guidePage, view }: { readonly guidePage?: PublicPage; readonly view: StandardView }) => {
  switch (view.kind) {
    case "page":
      if (view.phoneVerification !== undefined) return <PhoneStatusPanel status={view.phoneVerification} />
      if (view.entries !== undefined) return <RedesignBalanceView details={view.details} entries={view.entries} />
      return (
        <>
          <BalanceSummary values={view.details} />
          {view.items.length === 0 ? null : (
            <ul className="data-list">
              {view.items.map((item) => (
                <li key={item.id}>
                  <span>{item.label}</span>
                  <small>{item.meta}</small>
                </li>
              ))}
            </ul>
          )}
        </>
      )
    case "home":
      if (guidePage === undefined) throw new TypeError("HOME_GUIDE_PAGE_REQUIRED")
      return <HomeView guidePage={guidePage} view={view} />
    case "history":
      return view.details === undefined ? <RedesignHistoryList entries={view.entries} /> : <RedesignBalanceView details={view.details} entries={view.entries} />
    case "catalog":
      return <CatalogView view={view} />
    case "product":
      return <ProductView view={view} />
    case "transaction":
      if (view.domain === "redemption") return <OrderDetail view={view} />
      return (
        <>
          <section className="commerce-status-summary" data-commerce-result={view.domain}>
            <CommerceStatus status={view.serverState} />
            <p>{stateCopy(view)}</p>
          </section>
          <Details values={consumerTransactionDetails(view)} />
          <div className="notice">
            <p>
              처리 상태: <strong>{stateLabel(view.serverState)}</strong>
            </p>
            <p>거래 확인 번호: {maskedReference(view.reference)}</p>
          </div>
        </>
      )
    case "voucher":
      return (
        <>
          <Details
            values={[
              { label: "받는 번호", value: view.recipientMask },
              { label: "유효기간", value: dateFormatter.format(new Date(view.expiresAt)) },
            ]}
          />
          <section aria-label="발송 상태" className="commerce-status-summary">
            <CommerceStatus status={view.serverState} />
          </section>
          <div className="notice">
            <p>상품권은 등록된 번호로 MMS 발송됩니다. 이 화면에는 수신 정보와 발송 상태만 표시합니다.</p>
          </div>
        </>
      )
    case "commerce":
      return <CommerceView view={view} />
  }
}
